import {Collapse} from "@mui/material";
import React, {ComponentProps, useMemo} from "react";

import {useTranslation} from "../../../../i18n";
import {useFetchCareEntries} from "../../../../queries/useFetchCareEntries";
import Slide, {TransitionPosition} from "../../../animations/Slide";
import {CareEntryRowVariant, ExtendedCareEntrySearchResult} from "../../CareEntryRow";
import {TextInput} from "../../Input";
import {getSendClickedCareDiscoveryRowEvent} from "./careDiscoveryAnalytics";
import {getAllRelatedCares} from "./getAllRelatedCares";
import NoResults from "./NoResults";
import ResultList from "./ResultList";
import {useCareDiscoverySearchController} from "./useCareDiscoverySearchController";
import {useAreRecursiveTopicsEnabled} from "./useTopicStack";
import {SelectedLocation} from "@components/CareEntryRow/Control";

const emptyList: string[] = [];

const TopicScreen = ({
  care,
  selectedLocation,
  disableExpansion,
  in: _in,
  maybeToggleDialog,
  maybeAddCareToTopicStack,
  careEntryRowVariant,
  isParentSearchActive,
  from,
  viewId,
}: {
  care: ExtendedCareEntrySearchResult;
  selectedLocation?: SelectedLocation;
  disableExpansion?: boolean;
  from: TransitionPosition;
  maybeToggleDialog: ComponentProps<typeof ResultList>["maybeToggleDialog"];
  maybeAddCareToTopicStack: ComponentProps<typeof ResultList>["maybeAddCareToTopicStack"];
  careEntryRowVariant: CareEntryRowVariant;
  in?: boolean;
  isParentSearchActive?: boolean;
  viewId?: string;
}) => {
  const {t} = useTranslation();
  const shouldShowSearch = useAreRecursiveTopicsEnabled() && getAllRelatedCares(care).length > 8;
  const relatedTopicSlugs: string[] = useMemo(
    () => care.related_topics?.map(relatedTopic => relatedTopic.slug) || emptyList,
    [care.related_topics],
  );
  const careEntriesQuery = useFetchCareEntries(relatedTopicSlugs, {skip: disableExpansion});
  const careEntries = useMemo(
    () => [...careEntriesQuery.data, ...(care.cares || [])],
    [care.cares, careEntriesQuery.data],
  );

  const {
    isNoSymptomsSearched,
    handleQueryChange,
    searchCareEntriesQuery,
    showDefaultResults,
    showNoResults,
    resultsToRender,
  } = useCareDiscoverySearchController(careEntries);

  const loadingRowCount =
    showDefaultResults && careEntriesQuery.isLoading
      ? relatedTopicSlugs.length
      : searchCareEntriesQuery.isLoading
      ? 5
      : undefined;

  const reportClickEvent =
    isParentSearchActive || !showDefaultResults
      ? getSendClickedCareDiscoveryRowEvent("searched")
      : getSendClickedCareDiscoveryRowEvent("base");

  return (
    <Slide from={from} in={_in} duration={125} timeout={0} appear unmountOnExit>
      <div data-cy={`topic-expansion-${care.slug}`}>
        {shouldShowSearch && (
          <TextInput
            onChange={handleQueryChange}
            icon="search"
            placeholder={t("Search symptoms or services")}
            ariaLabel={t("Search symptoms or services")}
            data-cy={`topic-expansion-${care.slug}-discovery-search-input`}
            inputMode="text"
          />
        )}
        {showDefaultResults && (
          <span className="fs12 font-mr ttu gray600 pl-2 block pt-3">{care.name}</span>
        )}
        <Collapse in={showNoResults} unmountOnExit appear timeout={125}>
          <NoResults isNoSymptomsSearched={isNoSymptomsSearched} />
        </Collapse>
        <ResultList
          keyPrefix={`topic-expansion-results-${care.slug}`}
          selectedLocation={selectedLocation}
          careEntries={resultsToRender}
          loadingRowCount={loadingRowCount}
          transitionSpeed={0}
          hideListController
          rowVariant={careEntryRowVariant}
          hideBorders
          // random string toggles off the bold state
          highlightQuery="j22982jv9jvqa238vj29q"
          className="mt-2 gap-1"
          reportClickEvent={reportClickEvent}
          maybeToggleDialog={maybeToggleDialog}
          maybeAddCareToTopicStack={maybeAddCareToTopicStack}
          viewId={viewId}
        />
      </div>
    </Slide>
  );
};

export default React.memo(TopicScreen);
